import {
  ActionActivityDto,
  actionsFindCompletedForUser,
  actionsFriendActivity,
  actionsGetActionActivities,
  actionsGetActivityFeed,
  actionsLikeActivity,
  actionsUnlikeActivity,
} from "@alliance/shared/client";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../lib/AuthContext";
import posthog from "posthog-js";

export enum ActivityList {
  Friends = "friends",
  User = "user",
  Action = "action",
  Global = "global",
}

export type UseActivitiesProps = { comments?: boolean } & (
  | {
      list: ActivityList.User | ActivityList.Action;
      objectId: number;
    }
  | {
      list: ActivityList.Global | ActivityList.Friends;
      objectId?: never;
    }
);

const useActivities = ({
  list,
  objectId,
  comments = false,
}: UseActivitiesProps) => {
  const [activities, setActivities] = useState<ActionActivityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    let apiCall;
    switch (list) {
      case ActivityList.Friends:
        apiCall = actionsFriendActivity({ query: { comments } });
        break;
      case ActivityList.User:
        apiCall = actionsFindCompletedForUser({
          path: { id: objectId },
          query: { comments },
        });
        break;
      case ActivityList.Action:
        apiCall = actionsGetActionActivities({ path: { id: objectId } });
        break;
      case ActivityList.Global:
        apiCall = actionsGetActivityFeed({
          query: { limit: "50", before: new Date().toISOString(), comments },
        });
        break;
    }
    apiCall
      .then((resp) => {
        const respActivities = (resp.data ?? []).sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setActivities(respActivities);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [list, objectId, isAuthenticated, comments]);

  const handleLikeActivity = useCallback(
    async (activityId: number) => {
      if (!user) return;

      const activity = activities.find((a) => a.id === activityId);
      if (!activity) return;

      const isLiked = activity.likes.some((like) => like.id === user.id);

      if (isLiked) {
        const response = await actionsUnlikeActivity({
          path: { id: activityId },
        });
        if (response.response.ok) {
          setActivities((prev) =>
            prev.map((a) =>
              a.id === activityId
                ? {
                    ...a,
                    likes: a.likes.filter((like) => like.id !== user.id),
                  }
                : a
            )
          );
        }
      } else {
        const response = await actionsLikeActivity({
          path: { id: activityId },
        });
        if (response.response.ok && response.data) {
          setActivities((prev) =>
            prev.map((a) =>
              a.id === activityId
                ? {
                    ...a,
                    likes: response.data.likes || [],
                  }
                : a
            )
          );

          posthog.capture("activity_liked", {
            activityId: activity.id,
            activityType: activity.type,
          });
        }
      }
    },
    [user, activities]
  );

  const updateActivity = useCallback((updatedActivity: ActionActivityDto) => {
    setActivities((prev) =>
      prev.map((a) => (a.id === updatedActivity.id ? updatedActivity : a))
    );
  }, []);

  return {
    loading,
    activities,
    handleLikeActivity,
    setActivities,
    updateActivity,
  };
};

export default useActivities;
